import { ViewIds } from '@ardoq/api-types';
import { DropdownOptionType } from '@ardoq/dropdown-menu';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { IconName } from '@ardoq/icons';
import { t, type I18nLocale } from '@ardoq/i18n';
import { SettingsType, type SettingsConfig } from '@ardoq/view-settings';
import type { PolarViewSettings } from '@ardoq/xyz-charts';
import { handleStoreSetting } from 'viewSettings/settingsHelper';
import { markTypeDropdown } from '../markTypeMenu';

const VIEW_ID = ViewIds.POLAR;

type Field = { name: string; label: string };

type FieldPickerArgs = {
  id: string;
  key: keyof Pick<
    PolarViewSettings,
    | 'selectedFieldNameAngle'
    | 'selectedFieldNameRadius'
    | 'selectedFieldNameSeries'
  >;
  iconName: IconName;
  label: string;
  selected: string | null;
  fields: Field[];
  noneLabel: string | null;
};

const fieldPicker = ({
  id,
  key,
  iconName,
  label,
  selected,
  fields,
  noneLabel,
}: FieldPickerArgs): SettingsConfig => {
  const noneOption =
    noneLabel === null
      ? []
      : [
          {
            name: `${id}_none`,
            label: noneLabel,
            type: DropdownOptionType.OPTION,
            isActive: !fields.some(({ name }) => name === selected),
            onClick: () => handleStoreSetting(VIEW_ID, { [key]: null }),
          },
        ];
  return {
    id,
    type: SettingsType.DROPDOWN,
    iconName,
    label,
    buttonText: fields.find(({ name }) => name === selected)?.label ?? label,
    options: [
      ...noneOption,
      ...fields.map(({ name, label: fieldLabel }) => ({
        name: `${id}_${name}`,
        label: fieldLabel,
        type: DropdownOptionType.OPTION,
        isActive: selected === name,
        onClick: () => handleStoreSetting(VIEW_ID, { [key]: name }),
      })),
    ],
    isDisabled: fields.length === 0 && noneLabel === null,
    dropdownSize: DropdownSize.S,
  };
};

export type PolarViewModifiersArgs = {
  viewSettings: PolarViewSettings;
  numericFields: Field[];
  seriesFields: Field[];
  i18nLocale: I18nLocale;
};

export const getPolarViewModifiers = ({
  viewSettings,
  numericFields,
  seriesFields,
  i18nLocale,
}: PolarViewModifiersArgs): SettingsConfig[] => [
  fieldPicker({
    id: 'polarFieldAngle',
    key: 'selectedFieldNameAngle',
    iconName: IconName.ROTATE,
    label: t(i18nLocale, 'views:polar.menu.angleField', 'Angle field'),
    selected: viewSettings.selectedFieldNameAngle,
    fields: numericFields,
    noneLabel: null,
  }),
  fieldPicker({
    id: 'polarFieldRadius',
    key: 'selectedFieldNameRadius',
    iconName: IconName.ARROW_FORWARD,
    label: t(i18nLocale, 'views:polar.menu.radiusField', 'Radius field'),
    selected: viewSettings.selectedFieldNameRadius,
    fields: numericFields,
    noneLabel: null,
  }),
  fieldPicker({
    id: 'polarFieldSeries',
    key: 'selectedFieldNameSeries',
    iconName: IconName.PALETTE,
    label: t(i18nLocale, 'views:polar.menu.seriesField', 'Series by'),
    selected: viewSettings.selectedFieldNameSeries,
    fields: seriesFields,
    noneLabel: t(i18nLocale, 'views:polar.menu.seriesNone', 'None'),
  }),
  markTypeDropdown({
    viewId: VIEW_ID,
    id: 'polarMarkType',
    markType: viewSettings.markType,
    i18nLocale,
  }),
];
